import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DialogChatAPI from "../../api/dialogChatApi";
import PublicationsApi from "../../api/publicationsApi";
import CircleAvatar from "../../components/CircleAvatar/CircleAvatar";
import ModalWindow from "../../components/ModalWindow/ModalWindow";
import { Button } from "../../components/ui/button";
import { AvailableLangs } from "../../constans/languagesList";
import { ContextProvider } from "../../contextProvider";
import {
  AccessiblePublicationFile,
  Publication,
} from "../../types/Publications";
import { ReviewMessageModal } from "../ReviewSpecialists/components/ReviewMessageModal";
import PublicationArtwork from "./PublicationArtwork";
import PublicationMediaViewer from "./PublicationMediaViewer";
import {
  formatRequestDate,
  getHubLabel,
  getPublicationLifecycle,
  getPublisher,
  getUserLabel,
} from "./publicationQueueModel";
import s from "./Publications.module.css";

export type PublicationDecisionOutcome = {
  publication: Publication;
  message: string;
  tone: "success" | "error";
};

type ReviewMaterial = AccessiblePublicationFile & { expiresAt: number };

const withExpiry = (file: AccessiblePublicationFile): ReviewMaterial => ({
  ...file,
  expiresAt: Date.now() + Math.max(0, file.expiresIn) * 1000,
});

const getSpecializationLabel = (publication: Publication) => {
  const specialization = publication.mainSpecialization;
  if (!specialization) return "Not set";
  if (typeof specialization === "string") return "Unavailable";
  return (
    specialization.labels?.find(({ language }) => language === "en")?.text ??
    specialization.labels?.[0]?.text ??
    specialization.specialization ??
    "Unavailable"
  );
};

const formatCost = (cost: number) =>
  cost === 0 ? "Free" : `${(cost / 100).toFixed(2)} USD`;

const getMessageLanguage = (publication: Publication): AvailableLangs => {
  if (typeof publication.createdBy === "string") return "en";
  const language = publication.createdBy?.selectedLanguage;
  return language === "ru" || language === "uk" ? language : "en";
};

const PublicationInspector = ({
  publicationId,
  token,
  onDecisionResolved,
  readOnly = false,
}: {
  publicationId: string | null;
  token: string | null;
  onDecisionResolved?: (outcome: PublicationDecisionOutcome) => void;
  readOnly?: boolean;
}) => {
  const { userData } = useContext(ContextProvider);
  const [publication, setPublication] = useState<Publication | null>(null);
  const [materials, setMaterials] = useState<ReviewMaterial[]>([]);
  const [selectedMaterialId, setSelectedMaterialId] = useState<string | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [decisionKind, setDecisionKind] = useState<"approve" | "deny" | null>(
    null,
  );
  const [decisionFeedback, setDecisionFeedback] = useState<string | null>(null);
  const [isDecisionPending, setIsDecisionPending] = useState(false);
  const [retryVersion, setRetryVersion] = useState(0);
  const detailRequestId = useRef(0);
  const accessRequestId = useRef(0);

  const loadInspection = useCallback(
    async (knownDetail?: Publication) => {
      const requestId = ++detailRequestId.current;
      accessRequestId.current += 1;
      setPublication(null);
      setMaterials([]);
      setSelectedMaterialId(null);
      setPreviewError(null);

      if (!publicationId) {
        setIsLoading(false);
        setLoadError(null);
        return;
      }
      if (!token) {
        setIsLoading(false);
        setLoadError("Your admin session is unavailable.");
        return;
      }

      setIsLoading(true);
      setLoadError(null);
      try {
        const [detail, contentResponse] = await Promise.all([
          knownDetail ??
            PublicationsApi.getPublicationById(token, publicationId),
          PublicationsApi.getPublicationFiles(token, publicationId),
        ]);
        if (requestId !== detailRequestId.current) return;
        if (!detail) throw new Error("Publication is no longer accessible.");
        if (!contentResponse.status) {
          throw new Error(
            contentResponse.message || "Draft materials are inaccessible.",
          );
        }

        const nextMaterials = (contentResponse.files ?? [])
          .slice()
          .sort((left, right) => left.order - right.order)
          .map(withExpiry);
        setPublication(detail);
        setMaterials(nextMaterials);
        setSelectedMaterialId(nextMaterials[0]?._id ?? null);
      } catch (error) {
        if (requestId !== detailRequestId.current) return;
        console.error("Error loading Publication inspection:", error);
        setLoadError(
          error instanceof Error
            ? error.message
            : "Unable to load Publication inspection.",
        );
      } finally {
        if (requestId === detailRequestId.current) setIsLoading(false);
      }
    },
    [publicationId, token],
  );

  useEffect(() => {
    setDecisionFeedback(null);
    setDecisionKind(null);
    loadInspection();
    return () => {
      detailRequestId.current += 1;
      accessRequestId.current += 1;
    };
  }, [loadInspection, retryVersion]);

  const reportResolvedDecision = useCallback(
    (
      authoritative: Publication,
      message: string,
      tone: "success" | "error",
    ) => {
      setPublication(authoritative);
      setDecisionFeedback(message);
      setDecisionKind(null);
      onDecisionResolved?.({ publication: authoritative, message, tone });
    },
    [onDecisionResolved],
  );

  const sendAdminMessage = useCallback(
    async (message: string) => {
      if (!token || !userData?._id || !publication) return;
      const creatorId =
        typeof publication.createdBy === "string"
          ? publication.createdBy
          : publication.createdBy?._id;
      if (!creatorId) throw new Error("The creator could not be notified.");

      const chatResponse = await DialogChatAPI.createDialogChat(token, {
        userId: userData._id,
        userIdToSend: creatorId,
      });
      if (!chatResponse.status) throw new Error("Failed to create chat.");

      const messageResponse = await DialogChatAPI.sendMessage({
        userIdToSend: creatorId,
        message,
        userId: userData._id,
        date: new Date(),
        files: [],
      });
      if (!messageResponse.status) throw new Error("Failed to send message.");
    },
    [publication, token, userData],
  );

  const handleDecision = useCallback(
    async (kind: "approve" | "deny", message: string) => {
      if (isDecisionPending || !token || !publicationId || !publication) return;
      setIsDecisionPending(true);
      setDecisionFeedback(null);
      try {
        await sendAdminMessage(message);
        const fresh = await PublicationsApi.getPublicationById(
          token,
          publicationId,
        );
        if (!fresh) throw new Error("Publication is no longer accessible.");
        if (fresh.updatedAt !== publication.updatedAt) {
          await loadInspection(fresh);
          setDecisionKind(null);
          setDecisionFeedback(
            "This submission changed while you were reviewing it. The latest version is now loaded; review it again before deciding.",
          );
          return;
        }
        if (!fresh.isApproveRequested || fresh.deleted) {
          const result = fresh.isApproved
            ? "approved"
            : fresh.reviewDeclined
              ? "declined"
              : "closed";
          reportResolvedDecision(
            fresh,
            `Another administrator already ${result} this Publication. The authoritative result is shown.`,
            "error",
          );
          return;
        }

        let response;
        try {
          response = await PublicationsApi.updatePublication(
            token,
            publicationId,
            kind === "approve"
              ? { isApproved: true }
              : { reviewDeclined: true, reviewDeclineReason: message.trim() },
          );
        } catch (mutationError) {
          const authoritative = await PublicationsApi.getPublicationById(
            token,
            publicationId,
          );
          if (
            authoritative &&
            (!authoritative.isApproveRequested || authoritative.deleted)
          ) {
            const result = authoritative.isApproved
              ? "approved"
              : authoritative.reviewDeclined
                ? "declined"
                : "closed";
            reportResolvedDecision(
              authoritative,
              `Another administrator already ${result} this Publication. The authoritative result is shown.`,
              "error",
            );
            return;
          }
          throw mutationError;
        }

        const authoritative = await PublicationsApi.getPublicationById(
          token,
          publicationId,
        );
        if (!authoritative)
          throw new Error(
            "The decision was sent, but its current state could not be loaded.",
          );
        if (!response.status) {
          if (!authoritative.isApproveRequested || authoritative.deleted) {
            const result = authoritative.isApproved
              ? "approved"
              : authoritative.reviewDeclined
                ? "declined"
                : "closed";
            reportResolvedDecision(
              authoritative,
              `Another administrator already ${result} this Publication. The authoritative result is shown.`,
              "error",
            );
            return;
          }
          throw new Error(
            response.message || "The Publication decision was not accepted.",
          );
        }

        const matchesDecision =
          !authoritative.isApproveRequested &&
          (kind === "approve"
            ? authoritative.isApproved
            : authoritative.reviewDeclined);
        if (!matchesDecision) {
          reportResolvedDecision(
            authoritative,
            "The Publication changed during the decision. The authoritative result is shown; no success was reported.",
            "error",
          );
          return;
        }
        reportResolvedDecision(
          authoritative,
          kind === "approve"
            ? "Publication approved successfully."
            : "Publication declined and the feedback was saved.",
          "success",
        );
      } catch (error) {
        console.error("Error deciding Publication review:", error);
        setDecisionKind(null);
        setDecisionFeedback(
          error instanceof Error
            ? `${error.message} Review the current state and try again.`
            : "Unable to complete the Publication decision. Review the current state and try again.",
        );
      } finally {
        setIsDecisionPending(false);
      }
    },
    [
      isDecisionPending,
      loadInspection,
      publication,
      publicationId,
      reportResolvedDecision,
      sendAdminMessage,
      token,
    ],
  );

  const selectedMaterial = useMemo(
    () => materials.find(({ _id }) => _id === selectedMaterialId) ?? null,
    [materials, selectedMaterialId],
  );

  const refreshSelectedMaterial = useCallback(async () => {
    if (!token || !publicationId || !selectedMaterialId) return;
    const requestId = ++accessRequestId.current;
    setIsRefreshing(true);
    setPreviewError(null);
    try {
      const response = await PublicationsApi.getPublicationFileAccess(
        token,
        publicationId,
        selectedMaterialId,
      );
      if (requestId !== accessRequestId.current) return;
      if (!response.status || !response.file) {
        throw new Error(
          response.message || "Secure material access is unavailable.",
        );
      }
      const refreshed = withExpiry(response.file);
      setMaterials((current) =>
        current.map((material) =>
          material._id === selectedMaterialId ? refreshed : material,
        ),
      );
    } catch (error) {
      if (requestId !== accessRequestId.current) return;
      console.error("Error refreshing Publication material access:", error);
      setPreviewError(
        error instanceof Error
          ? error.message
          : "Unable to refresh secure preview.",
      );
    } finally {
      if (requestId === accessRequestId.current) setIsRefreshing(false);
    }
  }, [publicationId, selectedMaterialId, token]);

  useEffect(() => {
    if (!selectedMaterial) return;

    const refreshIn = selectedMaterial.expiresAt - Date.now() - 10000;
    if (refreshIn <= 0) {
      refreshSelectedMaterial();
      return;
    }
    const timeoutId = window.setTimeout(refreshSelectedMaterial, refreshIn);
    return () => window.clearTimeout(timeoutId);
  }, [refreshSelectedMaterial, selectedMaterial]);

  if (!publicationId) {
    return (
      <aside
        className={s.inspectorPlaceholder}
        aria-label="Publication inspection"
      >
        Select a Publication to inspect its current submission.
      </aside>
    );
  }
  if (isLoading) {
    return (
      <aside className={s.inspectorState} role="status">
        Loading current submission…
      </aside>
    );
  }
  if (loadError || !publication) {
    return (
      <aside className={s.inspectorState} role="alert">
        <strong>Submission could not be opened</strong>
        <span>{loadError || "Publication is inaccessible."}</span>
        <Button
          type="button"
          onClick={() => setRetryVersion((value) => value + 1)}
        >
          <RefreshIcon aria-hidden="true" /> Retry inspection
        </Button>
      </aside>
    );
  }

  const publisher = getPublisher(publication);
  const creatorId =
    typeof publication.createdBy === "string"
      ? publication.createdBy
      : publication.createdBy?._id;
  const isReviewable =
    !readOnly &&
    publication.isApproveRequested &&
    !publication.deleted &&
    materials.length > 0;
  return (
    <>
      <aside
        className={s.inspector}
        aria-label={`Inspect ${publication.title}`}
      >
        <div className={s.inspectorHeader}>
          <PublicationArtwork
            logoUrl={publication.logoUrl}
            kind={publication.kind}
            title={publication.title}
          />
          <div>
            <span className={s.eyebrow}>
              {readOnly ? "Read-only audit record" : "Current saved submission"}
            </span>
            <h2>{publication.title || "Untitled Publication"}</h2>
            <p>{publication.description || "No description provided."}</p>
          </div>
        </div>

        <div className={s.creatorRow}>
          <CircleAvatar
            userId={creatorId}
            width="44px"
            height="44px"
            fontSize="14px"
            borderRadius="50%"
          />
          <div className={s.creatorInfo}>
            <span className={s.eyebrow}>Author</span>
            <strong>{getUserLabel(publication.createdBy)}</strong>
          </div>
        </div>

        <dl className={s.detailsGrid}>
          <div>
            <dt>Lifecycle</dt>
            <dd>{getPublicationLifecycle(publication)}</dd>
          </div>
          <div>
            <dt>Kind</dt>
            <dd>{publication.kind ?? "Not set"}</dd>
          </div>
          <div>
            <dt>Language</dt>
            <dd>{publication.language.toUpperCase()}</dd>
          </div>

          <div>
            <dt>Cost</dt>
            <dd>{formatCost(publication.cost)}</dd>
          </div>
          <div>
            <dt>Publication ID</dt>
            <dd>{publication._id || publication.shortId || "Unavailable"}</dd>
          </div>
          <div>
            <dt>Creator</dt>
            <dd>{getUserLabel(publication.createdBy)}</dd>
          </div>
          <div>
            <dt>Publisher</dt>
            <dd>
              {publisher.type} · {publisher.name}
            </dd>
          </div>
          {publication.educationalHub && (
            <div>
              <dt>Hub affiliation</dt>
              <dd>{getHubLabel(publication.educationalHub)}</dd>
            </div>
          )}
          <div>
            <dt>Created</dt>
            <dd>{formatRequestDate(publication.createdAt)}</dd>
          </div>
          <div>
            <dt>Updated</dt>
            <dd>{formatRequestDate(publication.updatedAt)}</dd>
          </div>
          <div>
            <dt>Review requested</dt>
            <dd>{formatRequestDate(publication.approveRequestedAt)}</dd>
          </div>
          <div>
            <dt>Previously reviewed</dt>
            <dd>{formatRequestDate(publication.reviewedAt)}</dd>
          </div>
          <div>
            <dt>Previously approved</dt>
            <dd>{formatRequestDate(publication.approvedAt)}</dd>
          </div>
        </dl>

        {publication.isApproveRequested && publication.reviewDeclineReason && (
          <section
            className={s.historicalReason}
            aria-label="Historical decline feedback"
          >
            <strong>Previous review feedback — historical</strong>
            <p>{publication.reviewDeclineReason}</p>
            <span>This is not a decision on the current submission.</span>
          </section>
        )}

        <section
          className={s.materialsSection}
          aria-labelledby="materials-heading"
        >
          <div className={s.sectionHeading}>
            <div>
              <h3 id="materials-heading">
                {readOnly ? "Materials" : "Draft materials"}
              </h3>
              <span>{materials.length} total</span>
            </div>
            {!readOnly && materials.length === 0 && (
              <strong className={s.invalidBadge}>Invalid for approval</strong>
            )}
          </div>

          {materials.length === 0 ? (
            <div
              className={readOnly ? s.emptyMaterials : s.invalidMaterials}
              role={readOnly ? "status" : "alert"}
            >
              {readOnly
                ? "No materials are available for this audit record."
                : "This submission has no materials. At least one PDF or audio material is required."}
            </div>
          ) : (
            <div className={s.materialWorkspace}>
              <div
                className={s.materialList}
                aria-label="Ordered draft materials"
              >
                {materials.map((material) => (
                  <button
                    type="button"
                    key={material._id ?? material.order}
                    aria-pressed={selectedMaterialId === material._id}
                    className={`${s.materialItem} ${selectedMaterialId === material._id ? s.selectedMaterial : ""}`}
                    onClick={() => {
                      accessRequestId.current += 1;
                      setPreviewError(null);
                      setIsRefreshing(false);
                      setSelectedMaterialId(material._id ?? null);
                    }}
                  >
                    <span className={s.materialOrder}>{material.order}</span>
                    <span className={s.materialText}>
                      <strong>{material.title}</strong>
                      <span>
                        {material.description || "No material description."}
                      </span>
                      <small>
                        {material.format} ·{" "}
                        {material.isPreviewAvailable
                          ? "Client preview available"
                          : "Owner-only"}
                      </small>
                    </span>
                  </button>
                ))}
              </div>

              <div className={s.previewSurface}>
                {selectedMaterial ? (
                  <>
                    <div className={s.previewHeader}>
                      <div>
                        <strong>{selectedMaterial.title}</strong>
                        <span>
                          {selectedMaterial.format} · Order{" "}
                          {selectedMaterial.order}
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={refreshSelectedMaterial}
                        disabled={isRefreshing}
                      >
                        <RefreshIcon aria-hidden="true" /> Refresh preview
                      </Button>
                    </div>
                    <PublicationMediaViewer
                      material={selectedMaterial}
                      isRefreshing={isRefreshing}
                      error={previewError}
                      onRefresh={refreshSelectedMaterial}
                    />
                  </>
                ) : (
                  <div className={s.previewState}>
                    Select a material to preview it.
                  </div>
                )}
              </div>
            </div>
          )}
        </section>

        {!readOnly && (
          <section
            className={s.moderationSection}
            aria-labelledby="moderation-heading"
          >
            <div>
              <h3 id="moderation-heading">Moderation decision</h3>
              <p>
                Choose a decision and send a message to the publication author.
              </p>
            </div>
            {decisionFeedback && (
              <div className={s.decisionFeedback} role="alert">
                {decisionFeedback}
              </div>
            )}
            <div className={s.decisionActions}>
              <Button
                type="button"
                variant="success"
                disabled={!isReviewable || isDecisionPending}
                onClick={() => setDecisionKind("approve")}
              >
                <CheckCircleIcon aria-hidden="true" /> Approve
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={!isReviewable || isDecisionPending}
                onClick={() => setDecisionKind("deny")}
              >
                <CancelIcon aria-hidden="true" /> Decline
              </Button>
            </div>
          </section>
        )}
      </aside>

      <ModalWindow
        isOpen={decisionKind !== null}
        alternativeCloseFunction={() => {
          if (!isDecisionPending) setDecisionKind(null);
        }}
        disableBackdropClick={isDecisionPending}
        title="Publication moderation"
        width="fit-content"
      >
        {publication && decisionKind && (
          <ReviewMessageModal
            isApproval={decisionKind === "approve"}
            specialistName={getUserLabel(publication.createdBy)}
            onSend={(message) => handleDecision(decisionKind, message)}
            onCancel={() => {
              if (!isDecisionPending) setDecisionKind(null);
            }}
            userSelectedLang={getMessageLanguage(publication)}
            approvalTemplatesProps={{
              en: (name) =>
                `Dear ${name},\n\nCongratulations! Your Publication "${publication.title || "Untitled Publication"}" has been approved and is now available on Talzi.\n\nBest regards,\nTalzi moderation team`,
              ru: (name) =>
                `Уважаемый(ая) ${name},\n\nПоздравляем! Ваша публикация «${publication.title || "Без названия"}» была проверена и одобрена.\n\nС уважением,\nКоманда модерации Talzi`,
              uk: (name) =>
                `Шановний(а) ${name},\n\nВітаємо! Вашу публікацію «${publication.title || "Без назви"}» перевірено та схвалено.\n\nЗ повагою,\nКоманда модерації Talzi`,
            }}
            rejectionTemplatesProps={{
              en: (name, _specialization, fields) =>
                `Dear ${name},\n\nThank you for submitting your Publication "${publication.title || "Untitled Publication"}" for review.\n\nUnfortunately, we cannot approve it at this time. Please address the feedback below before resubmitting:\n\n${fields || "[Please provide specific reasons for rejection]"}\n\nBest regards,\nTalzi moderation team`,
              ru: (name, _specialization, fields) =>
                `Уважаемый(ая) ${name},\n\nСпасибо за отправку публикации «${publication.title || "Без названия"}» на модерацию.\n\nК сожалению, мы не можем одобрить её сейчас. Пожалуйста, внесите изменения согласно замечаниям ниже:\n\n${fields || "[Пожалуйста, укажите конкретные причины отклонения]"}\n\nС уважением,\nКоманда модерации Talzi`,
              uk: (name, _specialization, fields) =>
                `Шановний(а) ${name},\n\nДякуємо за подання публікації «${publication.title || "Без назви"}» на перевірку.\n\nНа жаль, ми не можемо схвалити її зараз. Будь ласка, внесіть зміни відповідно до зауважень нижче:\n\n${fields || "[Будь ласка, вкажіть конкретні причини відхилення]"}\n\nЗ повагою,\nКоманда модерації Talzi`,
            }}
          />
        )}
      </ModalWindow>
    </>
  );
};

export default PublicationInspector;
